import {isNil, get, isEmpty, sortBy, uniqBy} from 'lodash'

export const CurrencyDisplayFormat = {
  local: 0,
  international: 1,
}

export const getRoundValue = (value) => {
  return Math.round(value)
}

export const getInt = (value) => {
  const ret = parseInt(value)
  if (isNaN(ret)) return 0
  return ret
}

export const getFloat = (value) => {
  const ret = parseFloat(value)
  if (isNaN(ret)) return 0
  return ret
}

export const getUnicodeCharacter = (unicodePattern) => {
  if (isNil(unicodePattern))
    return ''

  const chars = unicodePattern.split(';').filter(char => !isEmpty(char))

  switch (chars.length) {
    case 0:
      return ''

    case 1: {
      const char1 = chars[0]
      return String.fromCharCode(char1)
    }

    case 2: {
      const char1 = chars[0]
      const char2 = chars[1]
      return String.fromCharCode(char1, char2)
    }

    default:
    case 3: {
      const char1 = chars[0]
      const char2 = chars[1]
      const char3 = chars[2]
      return String.fromCharCode(char1, char2, char3)
    }
  }
}

export const getDefaultCurrency = (currencies) => {
  return currencies.find(currency => currency.default)
}

export const getDefaultCurrencyId = (currencies) => {
  const defaultCurrency = currencies.find(currency => currency.default)
  if (defaultCurrency) return defaultCurrency.id
  return null
}

export const getCurrency = (currencyId, currencies) => {
  if (isEmpty(currencies)) return null
  return currencies.find(currency => currency.id === currencyId)
}

export const findCurrencyForShortDesc = (currencies, shortDesc) => {
  if (isEmpty(currencies)) return null
  return currencies.find(currency => currency.shortDesc === shortDesc)
}

export const getCurrencyPurchaseRate = (currency) => {
  return get(currency, 'rate', 1)
}

export const getCurrencySymbol = (currencyId, currencies) => {
  if (isNil(currencyId) || isNil(currencies)) {
    throw new Error('Invalid argument')
  } else {
    const currency = getCurrency(currencyId, currencies)
    return get(currency, 'shortDesc', null)
  }
}

export const getCurrencyUnicodePattern = (currencyId, currencies) => {
  const currency = getCurrency(currencyId, currencies)
  return get(currency, 'unicodePattern', null)

}

export const getCurrencyNameFor = (currencyId, currencies) => {
  const currency = getCurrency(currencyId, currencies)
  return get(currency, 'longDesc', null)
}

export const getCurrencyOption = (currency) => {
  const currencyName = currency.longDesc
  return {
    id: currency.id,
    value: currency.id,
    key: currency.id,
    label: currencyName,
    name: currencyName,
    currency,
  }
}

export const mapCurrenciesToOptions = (currencies = []) => {
  return currencies.map(currency => getCurrencyOption(currency))
}

export const getNumberString = (value, fixedDecimalPlaces) => {
  if (isNil(value)) return ''
  return formatRoundedNumber(Number(value), (fixedDecimalPlaces || 0))
}

export const getPercentageString = (value, fixedDecimalPlaces) => {
  if (isNil(value) || isNaN(value))
    return ''

  return formatRoundedNumber(Number(value), (fixedDecimalPlaces || 0)) + '%'
}

export const getPercentageOf = (percentage, value) => {
  if (isNil(percentage)) return 0
  return (parseFloat(percentage) / 100) * parseFloat(value)
}

export const getPercentageFor = (current, total) => {
  return current === 0 || total === 0
    ? 0
    : (current / total) * 100
}

export const getPriceFromMarkup = (cost, markup, currencyRate = 1) => {
  if (!isNil(markup) && !isNil(cost)) {
    markup = parseFloat(markup)
    const costInCurrency = parseFloat(cost) * currencyRate
    return (markup !== 0) ? getPercentageOf(markup, costInCurrency) + costInCurrency : costInCurrency
  } else {
    return 0
  }
}

export const getPriceFromMargin = (cost, margin, currencyRate = 1) => {
  if (!isNil(margin) && !isNil(cost)) {
    margin = parseFloat(margin)
    const costInCurrency = parseFloat(cost) * currencyRate
    return (margin < 100) ? costInCurrency * 100 / (100 - margin) : costInCurrency
  } else {
    return 0
  }
}

export const getNetPrice = (discount, totalPrice, referralFeePc = null) => {
  let ret = totalPrice
  if (!isNil(discount) && !isNil(totalPrice)) {
    discount = parseFloat(discount)
    totalPrice = parseFloat(totalPrice)
    ret -= getPercentageOf(discount, totalPrice)
  }

  if (!isNil(referralFeePc)) ret += getPercentageOf(parseFloat(referralFeePc), ret)
  return ret
}

// Calculate the referral value given the final net price and a referral fee %
export const getReferralValue = (netPrice, referralFeePc) => {
  if (isNil(referralFeePc)) return 0
  return netPrice * referralFeePc / (100 + referralFeePc)
}

// Calculate the equivalent discount given the total price, resultant net price and a referral fee %
export const getDiscount = (totalPrice, netPrice, referralFeePc) =>
  getDiscountWithReferralValue(totalPrice, netPrice, getReferralValue(netPrice, referralFeePc))

// Calculate the equivalent discount given the total price, resultant net price and referral currency value
export const getDiscountWithReferralValue = (totalPrice, netPrice, referralValue) => {
  let ret = 0
  if (!isNil(totalPrice) && !isNil(netPrice)) {
    let netPriceNoReferral = netPrice
    if (!isNil(referralValue)) netPriceNoReferral -= referralValue
    const discountValue = (totalPrice - netPriceNoReferral)
    ret = (totalPrice === 0) ? 0 : discountValue * 100 / totalPrice
  }
  return ret
}

export const convertToCurrency = (value, currencyRate) => value * currencyRate

export const getSupplierDiscount = (cost, msrp, pricingRate = 1) =>
  getSupplierDiscountFromCostInHomeCurrency(convertToCurrency(cost, pricingRate), msrp)

export const getSupplierDiscountFromCostInHomeCurrency = (costInHomeCurrency, msrp) => {
  return msrp === 0 ? 0 : (1 - (costInHomeCurrency / msrp)) * 100
}

export const getUnitCostFromMsrp = (msrp, supplierDiscount, pricingRate = 1) => {
  if (isNil(msrp) || isNil(supplierDiscount)) return 0
  msrp = parseFloat(msrp)
  supplierDiscount = parseFloat(supplierDiscount)
  return (msrp - (msrp * supplierDiscount / 100)) / pricingRate
}

export const getUnitPrice = (totalPrice, quantity) => {
  if (isNil(totalPrice) || isNil(quantity)) return 0
  totalPrice = parseFloat(totalPrice)
  quantity = parseInt(quantity)
  return totalPrice / quantity
}

export const getUnitCost = (totalCost, quantity) => {
  if (isNil(totalCost) || isNil(quantity)) return 0
  totalCost = parseFloat(totalCost)
  quantity = parseInt(quantity)
  return totalCost / quantity
}

export const getTotalValue = (unitValue, quantity) => {
  return getFloat(unitValue * quantity)
}

export const getMarkup = (price, cost, currencyRate = 1) => {
  if (!isNil(price) && !isNil(cost)) {
    const costInCurrency = parseFloat(cost) * currencyRate
    price = parseFloat(price)
    if (costInCurrency === 0) {
      return 0
    } else {
      return (price - costInCurrency) / costInCurrency * 100
    }
  } else {
    return 0
  }
}

export const getMargin = (price, cost, currencyRate = 1) => {
  if (!isNil(price) && !isNil(cost)) {
    price = parseFloat(price)
    const costInCurrency = parseFloat(cost) * currencyRate
    if (price === 0) {
      return 0
    } else {
      return ((price - costInCurrency) / price) * 100
    }
  } else {
    return 0
  }
}

export const getProfit = (netPrice, totalCost, currencyRate = 1) => {
  if (!isNil(netPrice) && !isNil(totalCost)) {
    return (parseFloat(netPrice) / parseFloat(currencyRate)) - parseFloat(totalCost)
  }
  return 0
}

export const round = (value, dps) => {
  const clean = 1 * value // convert to a number
  const fixed = (+(Math.round(+(clean + 'e' + dps)) + 'e' + -dps))
  return parseFloat(formatRoundedNumber(fixed, dps))
}

export const getFinancialValue = (value, precision = 2) => {
  const financialValue = isNil(value) || isNaN(value)
    ? formatRoundedNumber(parseFloat('0'), precision)
    : formatRoundedNumber(parseFloat(value), precision)

  return financialValue.replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
}

export const formatRoundedNumber = (number, precision) => {
  const tens = 10 ** precision
  const roundedNumber = Math.round(number * tens) / tens
  return roundedNumber.toFixed(precision)
}

export const getInitialCurrenciesList = (
  currencies,
  globalCurrencies,
  defaultCurrencies,
  showAllCurrencies
) => {

  const otherCurrencies = showAllCurrencies
    ? globalCurrencies
    : defaultCurrencies
      .map(shortDesc => findCurrencyForShortDesc(globalCurrencies, shortDesc))
      .filter(currency => !isNil(currency))

  return sortBy(uniqBy([...currencies, ...otherCurrencies], 'shortDesc'), ['shortDesc'])
}

export const getTotalProfit = (totalPrice, totalCost) => {
  if (!isNil(totalPrice) && !isNil(totalCost)) {
    return totalPrice - totalCost
  }
  return null
}

export default {
  CurrencyDisplayFormat,
  getInt,
  getFloat,
  getRoundValue,
  getPercentageString,
  getNumberString,
  getDefaultCurrency: getDefaultCurrencyId,
  getCurrencySymbol,
  getPercentageOf,
  getPriceFromMarkup,
  getPriceFromMargin,
  getUnitCostFromMsrp,
  getSupplierDiscount,
  getNetPrice,
  getTotalProfit,
  getUnitPrice,
  getMarkup,
  getMargin,
  getDiscount,
  getDiscountWithReferralValue,
  getReferralValue,
  getProfit,
  getUnicodeCharacter,
  round,
  formatRoundedNumber,
}
